import React from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const destructionCalls = [
  {
    title: "Retire Legacy Kick Control Procedures Involving Obsolete BOP Systems",
    rationale: "Misinformation risk from non-compliant equipment."
  },
  {
    title: "Remove PVT Models Not Calibrated to Shale Environments",
    rationale: "Inaccurate reservoir simulations under new operating assumptions."
  },
  {
    title: "Delete Unverified Lessons from Early EOR Pilots Without Validation Logs",
    rationale: "No traceability or data provenance."
  },
  {
    title: "Deactivate Procedures Using Non-API Certified Drillpipe Inspection Standards",
    rationale: "Safety and liability risk."
  },
  {
    title: "Eliminate Duplicative Entries for Flow Assurance in Lateral Sections",
    rationale: "Confusion due to inconsistent terminology."
  },
  {
    title: "Remove Manual NPT Logs Without Timestamps or Source Attribution",
    rationale: "Cannot be trusted for AI training."
  },
  {
    title: "Sunset Bit Selection Tables from 2010s Not Optimized for Current RPM/WOB Strategies",
    rationale: "Outdated tech references."
  },
  {
    title: "Remove Completion Recipes Based on Discontinued Fracturing Fluids",
    rationale: "Obsolete chemical safety data."
  },
  {
    title: "Discard Re-Entry Procedures That Don't Account for Modern Logging Tools",
    rationale: "Operational mismatch with modern tools."
  },
  {
    title: "Eliminate Safety Protocols Missing Remote Monitoring or AI Alert Integration",
    rationale: "Non-compliant with current digital HSE expectations."
  }
];

const ChevronCallForDestruction = () => {
  const navigate = useNavigate();

  const handleCallClick = (callTitle: string, rationale: string) => {
    // Navigate to the CfD flow with pre-populated Chevron-specific data
    navigate("/call-for-destruction/bounty", {
      state: {
        selectedArea: "Chevron Energy Operations",
        chevronCall: {
          title: callTitle,
          rationale: rationale,
          organization: "Chevron",
          bountyAmount: "0", // CfD gets no bounty
          bountyType: "Community",
          contributors: ["Trey Fleming", "Karen Watson", "Pete Dumont", "Giblet"],
          description: `Chevron requests the removal of outdated content: ${callTitle}. Rationale: ${rationale}`
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to="/chevron-channel">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Chevron Channel
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Oil and Gas Demonstration
          </h1>
          <p className="text-lg text-muted-foreground max-w-4xl mb-6">
            These are triggered when knowledge is outdated, duplicated, biased, or potentially unsafe.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Trash2 className="h-6 w-6" />
              Calls for Destruction (CfD)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {destructionCalls.map((call, index) => (
                <Card key={index} className="border border-muted hover:border-destructive/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-3">
                      <button 
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                        onClick={() => handleCallClick(call.title, call.rationale)}
                      >
                        CfD: {call.title}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChevronCallForDestruction;